import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import {
  HomeOutlined, BookOutlined, TrophyOutlined, ExperimentOutlined,
  ReadOutlined, DownloadOutlined, DollarOutlined, FormOutlined,
  PhoneOutlined, CloseOutlined, ThunderboltOutlined, DownOutlined, RightOutlined
} from "@ant-design/icons";

export default function Sidebar({ mobileOpen, onClose }) {
  const location = useLocation();
  const [coursesOpen, setCoursesOpen] = useState(false);
  const [courses, setCourses] = useState([]);

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/course/getAll`);
        // The API returns { data: [...], total: ..., totalPage: ... }
        setCourses(res?.data?.data || res?.data || []);
      } catch (e) {
        console.error("Failed to fetch courses for sidebar:", e);
      }
    };
    fetchCourses();
  }, []);

  const menu = [
    { label: "Trang Chủ", to: "/", icon: <HomeOutlined /> },
    { label: "Khóa Học", to: "/topic", icon: <BookOutlined />, hasSubmenu: true },
    { label: "Thi Đấu", to: "/competition", icon: <ThunderboltOutlined />, badge: "New" },
    { label: "Phương Pháp", to: "/method", icon: <ExperimentOutlined /> },
    { label: "Cách Học Chi Tiết", to: "/how", icon: <ReadOutlined /> },
    { label: "Bảng Giá", to: "/pricing", icon: <DollarOutlined /> },
    { label: "Blog", to: "/blog", icon: <FormOutlined /> },
    { label: "Liên Hệ", to: "/contact", icon: <PhoneOutlined /> },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const sidebarContent = (
    <div className="px-2 py-3">
      {/* Mobile close button */}
      <div className="md:hidden px-3 pb-3 mb-2 border-b border-white/10 flex items-center justify-between">
        <span className="text-white font-bold text-sm">VuaLingo</span>
        <button
          onClick={onClose}
          className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center text-gray-300"
        >
          <CloseOutlined className="text-xs" />
        </button>
      </div>
      <nav>
        <ul className="space-y-0.5">
          {menu.map((m) => (
            <li key={m.to}>
              {m.hasSubmenu ? (
                <>
                  <div
                    onClick={() => setCoursesOpen(!coursesOpen)}
                    className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all cursor-pointer ${
                      isActive(m.to)
                        ? "bg-blue-500/30 text-white"
                        : "text-gray-300 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className={`text-base ${isActive(m.to) ? "text-blue-300" : "text-gray-400"}`}>{m.icon}</span>
                      <span className="text-sm font-medium">{m.label}</span>
                    </div>
                    <span className="text-xs text-gray-500">
                      {coursesOpen ? <DownOutlined /> : <RightOutlined />}
                    </span>
                  </div>
                  <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    coursesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                  }`}>
                    <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-white/10 pl-3">
                      <li>
                        <Link
                          to="/topic"
                          onClick={onClose}
                          className={`block px-2 py-1.5 rounded-lg text-xs transition-all ${
                            location.pathname === '/topic'
                              ? 'bg-blue-500/20 text-blue-300 font-semibold'
                              : 'text-gray-400 hover:bg-white/10 hover:text-white'
                          }`}
                        >
                          📚 Xem tất cả
                        </Link>
                      </li>
                      {Array.isArray(courses) && courses.map((course) => (
                        <li key={course.id || course.slug}>
                          <Link
                            to={`/lesson/${course.slug}`}
                            onClick={onClose}
                            className={`block px-2 py-1.5 rounded-lg text-xs transition-all truncate ${
                              location.pathname === `/lesson/${course.slug}`
                                ? 'bg-blue-500/20 text-blue-300 font-semibold'
                                : 'text-gray-400 hover:bg-white/10 hover:text-white'
                            }`}
                          >
                            📖 {course.title}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </>
              ) : (
                <Link
                  to={m.to}
                  onClick={onClose}
                  className={`flex items-center justify-between px-3 py-2 rounded-lg transition-all ${
                    isActive(m.to)
                      ? "bg-blue-500/30 text-white"
                      : "text-gray-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className={`text-base ${isActive(m.to) ? "text-blue-300" : "text-gray-400"}`}>{m.icon}</span>
                    <span className="text-sm font-medium">{m.label}</span>
                  </div>
                  {m.badge && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${
                      isActive(m.to) ? "bg-white/20 text-white" : "bg-red-500 text-white"
                    }`}>{m.badge}</span>
                  )}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar — dark navy, liền mạch với header */}
      <aside className="hidden md:block w-52 bg-[#0f1e35] border-r border-white/10 min-h-screen sticky top-[48px] flex-shrink-0">
        {sidebarContent}
      </aside>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#0f1e35] border-r border-white/10 shadow-2xl overflow-y-auto animate-slideIn">
            {sidebarContent}
          </aside>
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slideIn {
          animation: slideIn 0.25s ease-out;
        }
      `}</style>
    </>
  );
}
